import React, { useState, useEffect, useCallback } from 'react';
import { useStripe } from '../contexts/StripeContext';
import { Elements } from '@stripe/react-stripe-js';
import stripePromise, { STRIPE_CONFIG } from '../config/stripe';
import PaymentMethodUpdate from '../components/PaymentMethodUpdate';
import SubscriptionManager from '../components/SubscriptionManager';
import { API_URLS } from '../constants/api';
import {
  CreditCard,
  AlertTriangle,
  CheckCircle,
  Loader,
  Trash2,
  Edit,
  ArrowLeft,
  RefreshCw
} from 'lucide-react';

const SubscriptionManagement = () => {
  const { subscription, loading, reloadSubscription, perpetualTrialMode } =
    useStripe();
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isResubscribing, setIsResubscribing] = useState(false);
  const [showPaymentUpdate, setShowPaymentUpdate] = useState(false);
  const [showSubscriptionManager, setShowSubscriptionManager] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [loadingPaymentMethod, setLoadingPaymentMethod] = useState(false);

  // Debug: Log when modal state changes
  useEffect(() => {
    console.log(
      'showSubscriptionManager state changed:',
      showSubscriptionManager
    );
  }, [showSubscriptionManager]);

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    return new Date(timestamp * 1000).toLocaleDateString();
  };

  const getStatusColor = (subscription) => {
    if (!subscription) return 'text-gray-600 bg-gray-50 border-gray-200';

    const status = subscription.status;
    const cancelAtPeriodEnd = subscription.cancelAtPeriodEnd;

    // Handle cancellation during trial or active
    if ((status === 'trialing' || status === 'active') && cancelAtPeriodEnd) {
      return 'text-orange-600 bg-orange-50 border-orange-200';
    }

    switch (status) {
      case 'trialing':
        return 'text-blue-600 bg-blue-50 border-blue-200';
      case 'active':
        return 'text-green-600 bg-green-50 border-green-200';
      case 'past_due':
        return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'canceled':
        return 'text-red-600 bg-red-50 border-red-200';
      case 'incomplete':
        return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'incomplete_expired':
        return 'text-red-600 bg-red-50 border-red-200';
      case 'unpaid':
        return 'text-red-600 bg-red-50 border-red-200';
      default:
        return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const getStatusText = (subscription) => {
    if (!subscription) return 'Unknown';

    const status = subscription.status;
    const cancelAtPeriodEnd = subscription.cancelAtPeriodEnd;

    // Handle cancellation during trial
    if (status === 'trialing' && cancelAtPeriodEnd) {
      return 'Trial Ending';
    }

    // Handle cancellation during active subscription
    if (status === 'active' && cancelAtPeriodEnd) {
      return 'Canceling';
    }

    switch (status) {
      case 'trialing':
        return 'Trial Active';
      case 'active':
        return 'Active';
      case 'past_due':
        return 'Past Due';
      case 'canceled':
        return 'Canceled';
      case 'incomplete':
        return 'Incomplete';
      case 'incomplete_expired':
        return 'Expired';
      case 'unpaid':
        return 'Unpaid';
      default:
        return status;
    }
  };

  const canCancelSubscription = (subscription) => {
    if (!subscription) return false;
    const status = subscription.status;
    const cancelAtPeriodEnd = subscription.cancelAtPeriodEnd;

    // Can't cancel if already canceled, incomplete, or already scheduled for cancellation
    if (
      status === 'canceled' ||
      status === 'incomplete' ||
      cancelAtPeriodEnd // Already scheduled for cancellation
    ) {
      return false;
    }

    return status === 'active' || status === 'trialing';
  };

  const canReactivate = (subscription) => {
    if (!subscription) return false;
    // Can reactivate if trial/active and scheduled for cancellation
    return (
      (subscription.status === 'trialing' ||
        subscription.status === 'active') &&
      subscription.cancelAtPeriodEnd === true
    );
  };

  const handleCancelSubscription = async () => {
    if (!subscription) return;

    setIsLoading(true);
    setMessage('');

    try {
      const response = await fetch(API_URLS.CANCEL_SUBSCRIPTION, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          subscriptionId: subscription.id
        })
      });

      const data = await response.json();
      console.log('Cancel subscription response:', {
        status: response.status,
        data
      });

      if (response.ok && data.subscription) {
        setMessage(
          'Subscription cancellation scheduled. You will retain access until the end of your billing period.'
        );
        setShowCancelConfirm(false);

        // Backend has already synced to Firebase, just reload from Firebase
        await reloadSubscription();
        console.log('Subscription refreshed after cancellation');
      } else {
        const errorMsg = data.error || 'Unknown error';
        console.error('Cancel subscription failed:', errorMsg);

        // Handle specific Stripe errors
        if (
          errorMsg.includes(
            'A canceled subscription can only update its cancellation_details'
          )
        ) {
          setMessage(
            'This subscription has already been canceled. Refreshing status...'
          );
          // Auto-refresh to show updated status
          try {
            await reloadSubscription();
            console.log('Subscription refreshed after already-canceled error');
          } catch (err) {
            console.error('Error refreshing subscription:', err);
            setMessage(
              'This subscription has already been canceled. Please refresh the page to see the updated status.'
            );
          }
        } else if (errorMsg.includes('No such subscription')) {
          setMessage(
            'This subscription no longer exists. It may have already been canceled or deleted. Refreshing status...'
          );
          // Auto-refresh to show updated status
          try {
            await reloadSubscription();
            console.log('Subscription refreshed after not-found error');
          } catch (err) {
            console.error('Error refreshing subscription:', err);
            setMessage(
              'This subscription no longer exists. It may have already been canceled or deleted. Please refresh the page.'
            );
          }
        } else if (errorMsg.includes('resource_missing')) {
          setMessage(
            'Subscription not found. It may have already been canceled. Refreshing status...'
          );
          // Auto-refresh to show updated status
          try {
            await reloadSubscription();
            console.log('Subscription refreshed after resource-missing error');
          } catch (err) {
            console.error('Error refreshing subscription:', err);
            setMessage(
              'Subscription not found. It may have already been canceled. Please refresh the page.'
            );
          }
        } else {
          setMessage(`Error canceling subscription: ${errorMsg}`);
        }
      }
    } catch (err) {
      console.error('Cancel subscription error:', err);
      setMessage('Failed to cancel subscription. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRefresh = useCallback(async () => {
    if (!subscription?.id) {
      setMessage('No subscription to refresh.');
      return;
    }

    setIsRefreshing(true);
    try {
      console.log('Refreshing subscription information:', subscription.id);

      const response = await fetch(API_URLS.REFRESH_SUBSCRIPTION, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          subscriptionId: subscription.id
        })
      });

      const data = await response.json();
      console.log('Refresh subscription response:', {
        status: response.status,
        data
      });

      if (response.ok && data.subscription) {
        // Backend has already synced to Firebase, just reload from Firebase
        await reloadSubscription();
        setMessage('Subscription status refreshed successfully.');
      } else {
        setMessage(
          `Error refreshing subscription: ${data.error || 'Unknown error'}`
        );
      }
    } catch (err) {
      console.error('Refresh subscription error:', err);
      setMessage('Failed to refresh subscription status. Please try again.');
    } finally {
      setIsRefreshing(false);
    }
  }, [subscription?.id, reloadSubscription]);

  const handleResubscribe = async () => {
    if (!subscription?.id) {
      setMessage('No subscription to resubscribe.');
      return;
    }

    setIsResubscribing(true);
    try {
      console.log('Resubscribing subscription:', subscription.id);

      const response = await fetch('/api/resubscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          subscriptionId: subscription.id
        })
      });

      const data = await response.json();
      console.log('Resubscribe response:', {
        status: response.status,
        data
      });

      if (response.ok && data.subscription) {
        // Backend has already synced to Firebase, just reload from Firebase
        await reloadSubscription();
        setMessage(
          'Subscription reactivated successfully! Your subscription will continue as normal.'
        );
      } else {
        setMessage(`Error resubscribing: ${data.error || 'Unknown error'}`);
      }
    } catch (err) {
      console.error('Resubscribe error:', err);
      setMessage('Failed to resubscribe. Please try again.');
    } finally {
      setIsResubscribing(false);
    }
  };

  const handlePaymentMethodUpdate = async (message) => {
    // Backend handles Firebase sync, just refresh and reload payment method
    setMessage(message);

    try {
      await reloadSubscription();
      await loadPaymentMethod(); // Reload payment method details
      console.log('Subscription refreshed after payment method update');
    } catch (err) {
      console.error(
        'Error refreshing subscription after payment method update:',
        err
      );
    }
  };

  // Load payment method details from backend
  const loadPaymentMethod = useCallback(async () => {
    if (!subscription?.id) return;

    setLoadingPaymentMethod(true);
    try {
      const response = await fetch(
        `/api/subscription/${subscription.id}/payment-method`
      );
      const data = await response.json();

      if (response.ok) {
        setPaymentMethod(data.paymentMethod);
      }
    } catch (err) {
      console.error('Error loading payment method:', err);
    } finally {
      setLoadingPaymentMethod(false);
    }
  }, [subscription?.id]);

  // Load payment method when subscription changes
  useEffect(() => {
    if (subscription?.id) {
      loadPaymentMethod();
    }
  }, [subscription?.id, loadPaymentMethod]);

  // Auto-refresh subscription when component mounts (but not in perpetual trial mode)
  useEffect(() => {
    if (subscription?.id && !perpetualTrialMode) {
      console.log('Auto-refreshing subscription on mount:', subscription.id);
      handleRefresh();
    }
  }, [subscription?.id, handleRefresh, perpetualTrialMode]); // Include dependencies

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <Loader className="h-8 w-8 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading subscription details...</p>
        </div>
      </div>
    );
  }

  if (!subscription) {
    return (
      <div className="min-h-screen bg-gray-50 py-12">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header with Back Button */}
          <div className="mb-6">
            <button
              onClick={() => window.history.back()}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-800 transition-colors"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to App
            </button>
          </div>

          <div className="bg-white rounded-lg shadow p-8 text-center">
            <CreditCard className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              {perpetualTrialMode
                ? 'Free Access Active'
                : 'No Active Subscription'}
            </h1>
            <p className="text-gray-600 mb-6">
              {perpetualTrialMode
                ? 'Enjoy unlimited access to all features while we prepare for launch. Subscriptions will be available soon!'
                : "You don't have an active subscription. Upgrade to Pro to access all features."}
            </p>
            <div className="flex gap-4 justify-center">
              <button
                onClick={() => window.history.back()}
                className="bg-gray-200 text-gray-800 px-6 py-3 rounded-lg hover:bg-gray-300 transition-colors"
              >
                Back to App
              </button>
              {!perpetualTrialMode && (
                <button
                  onClick={() => {
                    console.log('Upgrade to Pro button clicked');
                    setShowSubscriptionManager(true);
                  }}
                  className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Upgrade to Pro
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Subscription Manager Modal */}
        {showSubscriptionManager && !perpetualTrialMode && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
            <div className="w-full max-w-4xl max-h-[90vh] overflow-y-auto">
              <SubscriptionManager
                onClose={() => {
                  console.log('SubscriptionManager modal closing');
                  setShowSubscriptionManager(false);
                  reloadSubscription();
                }}
              />
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <button
              onClick={() => window.history.back()}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
            >
              <ArrowLeft className="h-5 w-5" />
              Back
            </button>
            {!perpetualTrialMode && (
              <button
                onClick={handleRefresh}
                disabled={isRefreshing}
                className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
              >
                <RefreshCw
                  className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`}
                />
                Refresh
              </button>
            )}
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Subscription Management
          </h1>
          <p className="text-gray-600">
            Manage your Docket Genie Pro subscription
          </p>
        </div>

        {/* Message Display */}
        {message && (
          <div
            className={`mb-6 p-4 rounded-lg ${
              message.includes('Error') || message.includes('Failed')
                ? 'bg-red-50 text-red-700 border border-red-200'
                : 'bg-green-50 text-green-700 border border-green-200'
            }`}
          >
            <div className="flex items-center gap-2">
              {message.includes('Error') || message.includes('Failed') ? (
                <AlertTriangle className="h-5 w-5" />
              ) : (
                <CheckCircle className="h-5 w-5" />
              )}
              <span>{message}</span>
            </div>
          </div>
        )}

        {/* Subscription Details */}
        <div className="bg-white rounded-lg shadow mb-6">
          <div className="p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-gray-900">
                Subscription Details
              </h2>
              <span
                className={`px-3 py-1 rounded-full text-sm font-medium border ${getStatusColor(subscription)}`}
              >
                {getStatusText(subscription)}
              </span>
            </div>

            {/* Incomplete Payment Notice */}
            {subscription?.status === 'incomplete' && (
              <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <div className="flex items-start">
                  <AlertTriangle className="h-5 w-5 text-yellow-600 mt-0.5 mr-3 flex-shrink-0" />
                  <div>
                    <h3 className="text-sm font-medium text-yellow-800">
                      Payment Incomplete
                    </h3>
                    <p className="text-sm text-yellow-700 mt-1">
                      Your payment is still being processed. This usually
                      happens when additional verification is required. Please
                      check your email for any payment confirmation requests, or
                      try updating your payment method.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Cancellation Notice */}
            {subscription?.status === 'active' &&
              subscription?.cancelAtPeriodEnd && (
                <div className="mb-6 p-4 bg-orange-50 border border-orange-200 rounded-lg">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start">
                      <AlertTriangle className="h-5 w-5 text-orange-600 mt-0.5 mr-3 flex-shrink-0" />
                      <div>
                        <h3 className="text-sm font-medium text-orange-800">
                          Subscription Scheduled for Cancellation
                        </h3>
                        <p className="text-sm text-orange-700 mt-1">
                          Your subscription will be canceled at the end of your
                          current billing period on{' '}
                          {formatDate(subscription.currentPeriodEnd)}.
                          You&apos;ll retain access to all Pro features until
                          then.
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={handleResubscribe}
                      disabled={isResubscribing}
                      className="ml-4 px-4 py-2 bg-orange-600 text-white text-sm font-medium rounded-lg hover:bg-orange-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isResubscribing ? (
                        <div className="flex items-center gap-2">
                          <Loader className="h-4 w-4 animate-spin" />
                          Resubscribing...
                        </div>
                      ) : (
                        'Resubscribe'
                      )}
                    </button>
                  </div>
                </div>
              )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-sm font-medium text-gray-500 mb-2">Plan</h3>
                <p className="text-lg font-semibold text-gray-900">Pro Plan</p>
                <p className="text-sm text-gray-600">
                  Unlimited searches, exports, and notifications
                </p>
              </div>

              <div>
                <h3 className="text-sm font-medium text-gray-500 mb-2">
                  Billing
                </h3>
                <p className="text-lg font-semibold text-gray-900">
                  $
                  {subscription.amount
                    ? (subscription.amount / 100).toFixed(2)
                    : STRIPE_CONFIG.plans.monthly.amount / 100}
                  /{subscription.interval === 'year' ? 'year' : 'month'}
                </p>
                <p className="text-sm text-gray-600">
                  Billed{' '}
                  {subscription.interval === 'year' ? 'annually' : 'monthly'}
                </p>
              </div>

              {/* Trial Period or Current Period - Hide in perpetual trial mode */}
              {!perpetualTrialMode && (
                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-2">
                    {subscription.status === 'trialing'
                      ? subscription.cancelAtPeriodEnd
                        ? 'Canceled Trial Period'
                        : 'Free Trial Period'
                      : subscription.status === 'canceled'
                        ? 'Final Period'
                        : subscription.status === 'incomplete'
                          ? 'Billing Period'
                          : 'Current Period'}
                  </h3>
                  <p className="text-gray-900">
                    {formatDate(subscription.currentPeriodStart)} -{' '}
                    {formatDate(subscription.currentPeriodEnd)}
                  </p>
                  {subscription.status === 'trialing' &&
                    !subscription.cancelAtPeriodEnd && (
                      <div className="mt-1">
                        <p className="text-sm text-blue-600 font-medium">
                          ✓ Trial active - No charge yet
                        </p>
                        <p className="text-xs text-gray-500 mt-0.5">
                          Full access to all Pro features
                        </p>
                      </div>
                    )}
                  {subscription.status === 'trialing' &&
                    subscription.cancelAtPeriodEnd && (
                      <div className="mt-1">
                        <p className="text-sm text-orange-600 font-medium">
                          ✓ Trial canceled - No charge
                        </p>
                        <p className="text-xs text-gray-500 mt-0.5">
                          Access until trial ends
                        </p>
                      </div>
                    )}
                  {subscription.status === 'canceled' && (
                    <p className="text-sm text-gray-600 mt-1">
                      This was your final billing period
                    </p>
                  )}
                  {subscription.status === 'incomplete' && (
                    <p className="text-sm text-yellow-600 mt-1">
                      Billing period will start once payment is complete
                    </p>
                  )}
                </div>
              )}

              {/* Next Billing Date or First Charge Date - Hide in perpetual trial mode */}
              {!perpetualTrialMode && (
                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-2">
                    {subscription.status === 'trialing' &&
                    subscription.cancelAtPeriodEnd
                      ? 'Access End Date'
                      : subscription.status === 'trialing'
                        ? 'First Charge Date'
                        : subscription.status === 'canceled'
                          ? 'Service End Date'
                          : subscription.status === 'incomplete'
                            ? 'Payment Required'
                            : 'Next Billing Date'}
                  </h3>
                  <p className="text-gray-900">
                    {subscription.status === 'incomplete'
                      ? 'Complete payment to activate'
                      : formatDate(subscription.currentPeriodEnd)}
                  </p>
                  {subscription.status === 'trialing' &&
                  subscription.cancelAtPeriodEnd ? (
                    <div className="mt-1">
                      <p className="text-sm text-orange-600 font-medium">
                        Trial ends - No charge
                      </p>
                      <p className="text-xs text-gray-500 mt-0.5">
                        Your access will end on this date. You will not be
                        charged.
                      </p>
                    </div>
                  ) : subscription.status === 'trialing' ? (
                    <div className="mt-1">
                      <p className="text-sm text-blue-600 font-medium">
                        Trial ends on this date
                      </p>
                      <p className="text-xs text-gray-500 mt-0.5">
                        You&apos;ll be charged $
                        {subscription.amount
                          ? (subscription.amount / 100).toFixed(2)
                          : STRIPE_CONFIG.plans.monthly.amount / 100}{' '}
                        on {formatDate(subscription.currentPeriodEnd)}
                      </p>
                    </div>
                  ) : subscription.status === 'canceled' ? (
                    <p className="text-sm text-gray-600 mt-1">
                      Service ended on this date
                    </p>
                  ) : subscription.status === 'incomplete' ? (
                    <p className="text-sm text-yellow-600 mt-1">
                      Complete payment to start your subscription
                    </p>
                  ) : subscription.cancelAtPeriodEnd ? (
                    <p className="text-sm text-orange-600 mt-1">
                      Service will end on this date
                    </p>
                  ) : (
                    <p className="text-sm text-gray-600 mt-1">
                      Next charge will occur on this date
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="bg-white rounded-lg shadow">
          <div className="p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              {perpetualTrialMode
                ? 'Free Access Status'
                : 'Manage Subscription'}
            </h2>

            {perpetualTrialMode && (
              <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 mr-3 flex-shrink-0" />
                  <div>
                    <h3 className="text-sm font-medium text-green-800">
                      Free Access Active
                    </h3>
                    <p className="text-sm text-green-700 mt-1">
                      You have unlimited access to all features while we prepare
                      for launch. Subscriptions will be available soon. No
                      payment required at this time.
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className="space-y-4">
              {/* Show message in perpetual trial mode */}
              {perpetualTrialMode ? (
                <div className="text-center py-8">
                  <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    Free Access Active
                  </h3>
                  <p className="text-gray-600">
                    Enjoy unlimited access to all features. No payment or
                    subscription management needed at this time.
                  </p>
                </div>
              ) : (
                <>
                  {/* Retry Payment for Incomplete Subscriptions */}
                  {subscription?.status === 'incomplete' && (
                    <div className="flex items-center justify-between p-4 border border-yellow-200 rounded-lg bg-yellow-50">
                      <div className="flex items-center gap-3">
                        <CreditCard className="h-5 w-5 text-yellow-600" />
                        <div>
                          <h3 className="font-medium text-gray-900">
                            Complete Payment
                          </h3>
                          <p className="text-sm text-gray-600">
                            Your payment needs to be completed to activate your
                            subscription
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => {
                          // For now, just refresh to check if payment completed
                          handleRefresh();
                        }}
                        className="flex items-center gap-2 text-yellow-700 hover:text-yellow-800 font-medium bg-yellow-100 hover:bg-yellow-200 px-4 py-2 rounded-lg transition-colors"
                      >
                        <RefreshCw className="h-4 w-4" />
                        Check Payment Status
                      </button>
                    </div>
                  )}

                  <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                    <div className="flex items-center gap-3">
                      <CreditCard className="h-5 w-5 text-gray-400" />
                      <div>
                        <h3 className="font-medium text-gray-900">
                          Payment Method
                        </h3>
                        {loadingPaymentMethod ? (
                          <p className="text-sm text-gray-600">Loading...</p>
                        ) : paymentMethod?.card ? (
                          <div className="text-sm text-gray-600">
                            <p className="font-medium">
                              {paymentMethod.card.brand.toUpperCase()} ••••{' '}
                              {paymentMethod.card.last4}
                            </p>
                            <p className="text-xs text-gray-500">
                              Expires{' '}
                              {paymentMethod.card.expMonth
                                .toString()
                                .padStart(2, '0')}
                              /{paymentMethod.card.expYear}
                            </p>
                          </div>
                        ) : (
                          <p className="text-sm text-gray-600">
                            No payment method on file
                          </p>
                        )}
                      </div>
                    </div>
                    <button
                      className="flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium"
                      onClick={() => setShowPaymentUpdate(true)}
                    >
                      <Edit className="h-4 w-4" />
                      {paymentMethod?.card ? 'Update' : 'Add'}
                    </button>
                  </div>

                  {canReactivate(subscription) ? (
                    <div className="flex items-center justify-between p-4 border border-orange-200 rounded-lg bg-orange-50">
                      <div className="flex items-center gap-3">
                        <CheckCircle className="h-5 w-5 text-orange-600" />
                        <div>
                          <h3 className="font-medium text-gray-900">
                            Reactivate{' '}
                            {subscription.status === 'trialing'
                              ? 'Trial'
                              : 'Subscription'}
                          </h3>
                          <p className="text-sm text-gray-600">
                            {subscription.status === 'trialing'
                              ? 'Continue your trial and get charged when it ends'
                              : 'Resume your subscription and continue billing'}
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={handleResubscribe}
                        disabled={isResubscribing}
                        className="flex items-center gap-2 px-4 py-2 bg-orange-600 text-white text-sm font-medium rounded-lg hover:bg-orange-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isResubscribing ? (
                          <>
                            <Loader className="h-4 w-4 animate-spin" />
                            Reactivating...
                          </>
                        ) : (
                          <>
                            <CheckCircle className="h-4 w-4" />
                            Reactivate
                          </>
                        )}
                      </button>
                    </div>
                  ) : canCancelSubscription(subscription) ? (
                    <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                      <div className="flex items-center gap-3">
                        <Trash2 className="h-5 w-5 text-gray-400" />
                        <div>
                          <h3 className="font-medium text-gray-900">
                            Cancel Subscription
                          </h3>
                          <p className="text-sm text-gray-600">
                            Cancel your subscription (you&apos;ll retain access
                            until the end of your billing period)
                          </p>
                        </div>
                      </div>
                      <button
                        className="flex items-center gap-2 text-red-600 hover:text-red-700 font-medium"
                        onClick={() => setShowCancelConfirm(true)}
                      >
                        <Trash2 className="h-4 w-4" />
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg bg-gray-50">
                      <div className="flex items-center gap-3">
                        <Trash2 className="h-5 w-5 text-gray-400" />
                        <div>
                          <h3 className="font-medium text-gray-900">
                            Cancel Subscription
                          </h3>
                          <p className="text-sm text-gray-600">
                            {subscription?.status === 'canceled'
                              ? 'This subscription has already been canceled.'
                              : subscription?.status === 'incomplete'
                                ? 'This subscription cannot be canceled until payment is complete.'
                                : subscription?.cancelAtPeriodEnd
                                  ? 'This subscription is already scheduled for cancellation at the end of the billing period.'
                                  : 'This subscription cannot be canceled at this time.'}
                          </p>
                        </div>
                      </div>
                      <span className="text-sm text-gray-500">
                        {getStatusText(subscription)}
                      </span>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        {/* Cancel Confirmation Modal */}
        {showCancelConfirm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-md w-full p-6">
              <div className="flex items-center gap-3 mb-4">
                <AlertTriangle className="h-6 w-6 text-red-600" />
                <h3 className="text-lg font-semibold text-gray-900">
                  Cancel Subscription
                </h3>
              </div>

              <p className="text-gray-600 mb-6">
                Are you sure you want to cancel your subscription? Your
                subscription will be canceled at the end of your current billing
                period ({formatDate(subscription.currentPeriodEnd)}), and
                you&apos;ll retain access to all Pro features until then.
              </p>

              <div className="flex gap-3">
                <button
                  onClick={handleCancelSubscription}
                  disabled={isLoading}
                  className="flex-1 bg-red-600 text-white py-2 px-4 rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isLoading ? (
                    <>
                      <Loader className="h-4 w-4 animate-spin" />
                      Canceling...
                    </>
                  ) : (
                    'Yes, Cancel Subscription'
                  )}
                </button>
                <button
                  onClick={() => setShowCancelConfirm(false)}
                  className="flex-1 bg-gray-200 text-gray-800 py-2 px-4 rounded-lg hover:bg-gray-300"
                >
                  Keep Subscription
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Payment Method Update Modal */}
      <PaymentMethodUpdate
        isOpen={showPaymentUpdate}
        onClose={() => setShowPaymentUpdate(false)}
        onSuccess={handlePaymentMethodUpdate}
        subscriptionId={subscription?.id}
      />
    </div>
  );
};

const SubscriptionManagementPage = () => {
  return (
    <Elements stripe={stripePromise}>
      <SubscriptionManagement />
    </Elements>
  );
};

export default SubscriptionManagementPage;
